import { memo, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Select, DatePicker, Tooltip, Checkbox } from "antd";
import dayjs from "dayjs";
import { Trash2 } from "lucide-react";

import { Input } from "../../../components/ui/input";
import { OPTION_SOURCES as DEFAULT_OPTION_SOURCES, validateRow as defaultValidateRow } from "../migrationConfig";

// Row-only virtualization with native horizontal scroll.
// - One scroll container handles BOTH axes, so the sticky header and the body
//   share the same width and never desync.
// - Only the ~visible rows are mounted, and each cell renders a CHEAP read-only
//   display until it is focused — the heavy antd control is mounted only for the
//   cell being edited. This keeps scrolling smooth (no blank rows from mounting
//   hundreds of Selects) and keeps focus while typing (the editor never
//   unmounts mid-edit, and validation styling lives on the display, not the
//   live input).
const ROW_H = 56;     // height of one data row (control ~40px + padding)
const HEADER_H = 44;  // sticky header height
const OVERSCAN = 16;  // rows rendered beyond the viewport (buffers fast scroll)

const PLACEHOLDER = {
    select: "Select",
    enum: "Select",
    date: "YYYY-MM-DD",
    password: "",
    text: "",
};

const displayValue = (field, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (field.kind === "password") return "••••••";
    return String(value);
};

// A single cell: read-only chip by default, swaps to a real control on focus.
const Cell = memo(({ field, value, error, options, optionSources, rowKey, onChange }) => {
    const [editing, setEditing] = useState(false);
    const stop = () => setEditing(false);
    const commit = (v) => onChange(rowKey, field.key, v);

    // --- display mode: a plain bordered box (cheap to mount/scroll) ----------
    if (!editing) {
        const text = displayValue(field, value);
        return (
            <Tooltip title={error || ""}>
                <div
                    tabIndex={0}
                    role="textbox"
                    onClick={() => setEditing(true)}
                    onFocus={() => setEditing(true)}
                    className={[
                        "w-full h-10 px-3 flex items-center rounded-md border text-sm cursor-text truncate",
                        "bg-white dark:bg-gray-700 transition-colors",
                        error ? "border-red-400" : "border-gray-300 dark:border-gray-600",
                        text ? "text-gray-900 dark:text-gray-100" : "text-gray-400",
                    ].join(" ")}
                >
                    {text || PLACEHOLDER[field.kind] || ""}
                </div>
            </Tooltip>
        );
    }

    // --- edit mode: the heavy control, mounted only for this one cell --------
    // No error styling here so validation never interrupts active typing.
    if (field.kind === "select" || field.kind === "enum") {
        const opts =
            field.kind === "enum"
                ? field.enum.map((o) => ({ value: o.text, label: o.text }))
                : (options[field.source] || []).map((o) => {
                      const label = optionSources[field.source].label;
                      return { value: o[label], label: o[label] };
                  });
        return (
            <Select
                size="large"
                showSearch
                autoFocus
                defaultOpen
                className="w-full"
                optionFilterProp="label"
                value={value || undefined}
                options={opts}
                onChange={commit}
                onBlur={stop}
                onDropdownVisibleChange={(open) => { if (!open) stop(); }}
            />
        );
    }
    if (field.kind === "date") {
        const iso = value ? String(value).trim().slice(0, 10) : "";
        const parsed = /^\d{4}-\d{2}-\d{2}$/.test(iso) && dayjs(iso).isValid() ? dayjs(iso) : null;
        return (
            <DatePicker
                size="large"
                autoFocus
                defaultOpen
                allowClear
                inputReadOnly
                className="w-full"
                format="YYYY-MM-DD"
                value={parsed}
                onChange={(_, str) => {
                    commit(str || "");
                    stop();
                }}
                onOpenChange={(open) => { if (!open) stop(); }}
                getPopupContainer={() => document.body}
            />
        );
    }
    return (
        <Input
            type={field.kind === "password" ? "password" : "text"}
            autoFocus
            value={value ?? ""}
            onChange={(e) => commit(e.target.value)}
            onBlur={stop}
            className="w-full bg-white dark:bg-gray-700"
            autoComplete={field.kind === "password" ? "new-password" : "off"}
        />
    );
});

// One row. Computes its own validation so unchanged rows keep prop identity and
// the memo skips them while editing elsewhere.
const GridRow = memo(({ row, columns, options, optionSources, validateRow, onChange, onDelete, selected, onToggleKey, top, width }) => {
    const errors = useMemo(() => validateRow(row, options), [row, options, validateRow]);
    return (
        <div
            className={[
                "absolute left-0 flex border-b border-gray-100 dark:border-gray-700",
                selected ? "bg-sidebar-primary/5" : "hover:bg-gray-50/60 dark:hover:bg-gray-700/30",
            ].join(" ")}
            style={{ top: 0, transform: `translateY(${top}px)`, height: ROW_H, width }}
        >
            {columns.map((col) => (
                <div
                    key={col.key}
                    className="px-1.5 flex items-center box-border"
                    style={{ flex: `0 0 ${col.width}px`, width: col.width }}
                >
                    {col.select ? (
                        <Checkbox
                            className="mx-auto"
                            checked={selected}
                            onChange={() => onToggleKey(row.__key)}
                        />
                    ) : col.action ? (
                        <Tooltip title="Remove row">
                            <button
                                className="mx-auto text-gray-400 hover:text-red-500"
                                onClick={() => onDelete(row)}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </Tooltip>
                    ) : (
                        <Cell
                            field={col.field}
                            value={row[col.field.key]}
                            error={errors[col.field.key]}
                            options={options}
                            optionSources={optionSources}
                            rowKey={row.__key}
                            onChange={onChange}
                        />
                    )}
                </div>
            ))}
        </div>
    );
});

const MigrationGrid = ({
    rows,
    columns,
    options,
    validateRow = defaultValidateRow,
    optionSources = DEFAULT_OPTION_SOURCES,
    onChange,
    onDelete,
    selectedKeys,
    onToggleKey,
    headerChecked = false,
    headerIndeterminate = false,
    onToggleAll,
    height = 420,
}) => {
    const parentRef = useRef(null);
    const totalWidth = useMemo(() => columns.reduce((s, c) => s + c.width, 0), [columns]);

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ROW_H,
        overscan: OVERSCAN,
        // The sticky header occupies the top of the scroll content; offset the
        // virtual range so row positions line up below it.
        scrollMargin: HEADER_H,
        getItemKey: (index) => rows[index]?.__key ?? index,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    return (
        <div
            ref={parentRef}
            className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            style={{ height }}
        >
            <div style={{ width: totalWidth, position: "relative" }}>
                {/* Sticky header — scrolls horizontally with the body, pinned vertically */}
                <div
                    className="sticky top-0 z-10 flex bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
                    style={{ width: totalWidth, height: HEADER_H }}
                >
                    {columns.map((col) => (
                        <div
                            key={col.key}
                            className="px-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300 box-border"
                            style={{ flex: `0 0 ${col.width}px`, width: col.width }}
                        >
                            {col.select ? (
                                <Checkbox
                                    className="mx-auto"
                                    checked={headerChecked}
                                    indeterminate={headerIndeterminate}
                                    onChange={(e) => onToggleAll?.(e.target.checked)}
                                />
                            ) : (
                                <span className="truncate">
                                    {col.title}
                                    {col.required && <span className="text-red-500"> *</span>}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Virtualized body */}
                <div style={{ height: rowVirtualizer.getTotalSize(), width: totalWidth, position: "relative" }}>
                    {virtualItems.map((vi) => {
                        const row = rows[vi.index];
                        if (!row) return null;
                        return (
                            <GridRow
                                key={row.__key}
                                row={row}
                                columns={columns}
                                options={options}
                                optionSources={optionSources}
                                validateRow={validateRow}
                                onChange={onChange}
                                onDelete={onDelete}
                                selected={selectedKeys?.has(row.__key) || false}
                                onToggleKey={onToggleKey}
                                width={totalWidth}
                                top={vi.start - rowVirtualizer.options.scrollMargin}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MigrationGrid;
